import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

export type SubscriptionCancelChoice = { immediate: boolean; reason: string };

export type SubscriptionCancelPanelProps = {
  /** 결제 기간 끝에 해지(기본). false 면 이미 예약되었거나 지원하지 않는다. */
  allowPeriodEnd: boolean;
  /** 지금 해지. Paddle 구독만 지원한다. */
  allowImmediate: boolean;
  /** 이미 예약된 해지 시각(표시용 문자열). */
  scheduledEndLabel?: string | null;
  defaultReason: string;
  busy: boolean;
  onConfirm: (choice: SubscriptionCancelChoice) => void | Promise<void>;
  /** 시험·스크린샷용: 처음부터 펼친 상태. */
  initiallyOpen?: boolean;
  testId?: string;
};

/**
 * 구독 해지 패널. 해지는 다음 결제를 멈추는 일이고 환불이 아니다 — 문구로 분명히 한다.
 * 기간 끝 해지와 즉시 해지 중 하나를 고르고, 사유를 적고, 확정한다.
 */
export function SubscriptionCancelPanel({
  allowPeriodEnd,
  allowImmediate,
  scheduledEndLabel = null,
  defaultReason,
  busy,
  onConfirm,
  initiallyOpen = false,
  testId = 'subscription-cancel',
}: SubscriptionCancelPanelProps) {
  const [open, setOpen] = useState(initiallyOpen);
  const [immediate, setImmediate] = useState(!allowPeriodEnd);
  const [reason, setReason] = useState(defaultReason);

  if (!allowPeriodEnd && !allowImmediate) return null;

  if (!open) {
    return (
      <div className="flex flex-wrap items-center gap-2" data-testid={testId}>
        {scheduledEndLabel && (
          <span className="text-sm text-amber-900">해지 예약됨 · {scheduledEndLabel}에 이용 종료</span>
        )}
        <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)} data-testid={`${testId}-open`}>
          {allowPeriodEnd ? '구독 해지…' : '즉시 해지…'}
        </Button>
      </div>
    );
  }

  const confirmLabel = immediate ? '지금 해지(환불 없음)' : '기간 끝에 해지';
  return (
    <div className="space-y-3 rounded-md border border-amber-300/70 bg-amber-50/60 p-3" data-testid={`${testId}-panel`}>
      <p className="text-sm text-amber-950">
        해지는 다음 결제를 멈추는 일입니다. 이미 낸 돈은 돌려주지 않습니다 — 돈을 돌려주려면 주문의 <b>환불</b>을 쓰세요.
      </p>
      <fieldset className="space-y-1.5 text-sm">
        <legend className="sr-only">해지 방식</legend>
        {allowPeriodEnd && (
          <label className="flex items-start gap-2">
            <input type="radio" name={`${testId}-mode`} checked={!immediate} onChange={() => setImmediate(false)} />
            <span><b>기간 끝까지 이용</b> — 이번 결제 기간이 끝나면 해지(권장)</span>
          </label>
        )}
        {allowImmediate && (
          <label className="flex items-start gap-2">
            <input type="radio" name={`${testId}-mode`} checked={immediate} onChange={() => setImmediate(true)} />
            <span><b>즉시 해지</b> — 지금 이용이 끝납니다. 남은 기간 환불 없음</span>
          </label>
        )}
      </fieldset>
      <label className="block space-y-1 text-sm">
        <span className="text-muted-foreground">해지 사유(기록에 남음)</span>
        <Input value={reason} maxLength={500} onChange={(event) => setReason(event.target.value)} />
      </label>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant={immediate ? 'destructive' : 'default'}
          disabled={busy || !reason.trim()}
          onClick={() => void onConfirm({ immediate, reason: reason.trim() })}
          data-testid={`${testId}-confirm`}
        >
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {confirmLabel}
        </Button>
        <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => setOpen(false)}>
          닫기
        </Button>
      </div>
    </div>
  );
}
